import { HttpErrorResponse } from '@angular/common/http';
import { IResponse } from '../models/chat.models';

/** Just the two envelope fields these helpers need, so any IResponse<T> is accepted. */
type ApiEnvelope = Pick<IResponse<unknown>, 'succeeded' | 'message'>;

/** A support reference code: short, alphanumeric, and always carrying at least one digit. */
const REFERENCE_CODE = String.raw`(?=[A-Za-z0-9-]*\d)[A-Za-z0-9-]{4,20}`;

/** The parenthesised form, e.g. "The AI service failed. (reference code a1b2c3d4)". */
const PARENTHESISED_REFERENCE = new RegExp(String.raw`\s*\(\s*reference\s+code\s+${REFERENCE_CODE}\s*\)`, 'gi');

/** The embedded clause, e.g. "Please try again or contact support with reference code a1b2c3d4". */
const REFERENCE_CLAUSE = new RegExp(
  String.raw`[\s,;]*(?:\b(?:and|or)\b\s+)?(?:please\s+)?(?:contact\s+support\s+(?:with|using|quoting)\s+)?reference\s+code\s+${REFERENCE_CODE}`,
  'gi'
);

/**
 * PLAN-2314: drops the support reference code from a backend message before it is shown.
 *
 * The code is meaningless to the user and makes the inline panel message longer than it needs to be; it
 * still travels on the failed response and sits in the backend logs, so support can correlate a report
 * without the user quoting it.
 *
 * A message that carries no code is returned byte-identical — the tidy-up below only runs when something
 * was actually removed, so unrelated messages never gain or lose punctuation. The digit lookahead keeps a
 * message that merely says the words ("No reference code here") intact.
 */
export function stripSupportReferenceCode(message: string): string {
  const withoutCode = message.replace(PARENTHESISED_REFERENCE, '').replace(REFERENCE_CLAUSE, '');
  if (withoutCode === message) {
    return message;
  }

  return tidyAfterRemoval(withoutCode);
}

/** Closes the gap the removed clause left: no dangling connector punctuation, exactly one closing period. */
function tidyAfterRemoval(message: string): string {
  const trimmed = message
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[\s,;:]+$/, '')
    .replace(/\.{2,}$/, '.');

  if (!hasWords(trimmed)) {
    return '';
  }

  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function hasWords(message: string): boolean {
  return /[A-Za-z0-9]/.test(message);
}

/**
 * The single place a backend message becomes displayable. Both entry points below funnel through it, so
 * the thrown-error path and the succeeded:false path cannot drift apart again — the first version of
 * PLAN-2314 stripped the reference code in only one of them and leaked it in the other.
 */
function cleanMessage(message: unknown, fallback: string): string {
  const cleaned = typeof message === 'string' ? stripSupportReferenceCode(message) : '';

  return cleaned.trim() ? cleaned : fallback;
}

/**
 * DATA-1351: API failures from mcp-gx-data-client arrive as non-2xx responses whose body keeps the
 * {data, message, succeeded} envelope. The message is user-friendly, so it is shown as-is apart from the
 * support reference code that PLAN-2314 strips. The fallback covers network failures, non-envelope bodies
 * (e.g. FastAPI 422 validation errors) and the case where the message was nothing but a reference code.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const envelope = error instanceof HttpErrorResponse ? error.error : undefined;
  const message = envelope?.succeeded === false ? envelope?.message : undefined;

  return cleanMessage(message, fallback);
}

/**
 * The counterpart for a failure that arrives as a **200** whose envelope says `succeeded: false` — the
 * backend uses that shape too, so it never reaches an `error` callback and `getApiErrorMessage` above
 * never sees it. Use this instead of reading `response.message` directly, or the support reference code
 * reaches the screen.
 */
export function getResponseErrorMessage(response: ApiEnvelope | null | undefined, fallback: string): string {
  const message = response?.succeeded === false ? response.message : undefined;

  return cleanMessage(message, fallback);
}
